import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'
import type { UseLoadMorePayload } from '@domain-group/react-hooks'

import useSelector from '@hooks/useSelector'
import useReduxAction from '@hooks/useReduxAction'

import type { EntityOptions } from '@redux/modules/module'
import type { ModuleState } from '@redux/modules/types'

import type { CampaignPortalSyncModel } from '@models/types'

const watchEntityKeys = ['campaignPortalSyncs']

type CampaignPortalSyncsFilters = {
  agency_id?: number,
  campaign_id?: number,
  sync_type?: string,
}

type Options = {
  entityKey?: string,
  filters?: CampaignPortalSyncsFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

interface UseCampaignPortalSyncsPayload {
  callbacks: {
    loadMore: () => void,
  },
  canLoadMore: boolean,
  filteredCampaignPortalSyncs: CampaignPortalSyncModel[],
  filteredCampaignPortalSyncsCount: number,
  hasFilteredCampaignPortalSyncs: boolean,
  loading: boolean,
}

function useCampaignPortalSyncs(options: Options = {}): UseCampaignPortalSyncsPayload {
  const { filters, requestOptions } = options

  const {
    agency_id: filterAgencyId,
    campaign_id: filterCampaignId,
    sync_type: filterSyncType,
  } = filters || {}

  const {
    updatedEntities: { campaignPortalSyncs: campaignPortalSyncsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaigns, campaignPortalSyncs } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignPortalSyncs = useMemo((): CampaignPortalSyncModel[] => {
    const filtered = Object.values(campaignPortalSyncs).filter((campaignPortalSync) => {
      const { campaign_id, sync_type } = campaignPortalSync

      const campaign = campaigns[campaign_id] || {}
      const { agency_id } = campaign

      const matchAgency = matchFilterNumber(agency_id, filterAgencyId)
      const matchCampaign: boolean = matchFilterNumber(campaign_id, filterCampaignId)
      const syncMatch = filterSyncType ? filterSyncType === sync_type : true

      return matchAgency && matchCampaign && syncMatch
    })

    return sortArrayBy(filtered, 'desc', 'id')
  }, [campaignPortalSyncsUpdatedAt, JSON.stringify(filters)])

  const filteredCampaignPortalSyncsCount: number = filteredCampaignPortalSyncs.length

  const loadMorePayload: UseLoadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredCampaignPortalSyncsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCampaignPortalSyncs } = useReduxAction(
    'campaignPortalSyncs',
    'loadCampaignPortalSyncs',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  const hasFilteredCampaignPortalSyncs = !!filteredCampaignPortalSyncs.length

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaignPortalSyncs,
    filteredCampaignPortalSyncsCount,
    hasFilteredCampaignPortalSyncs,
    loading: loadingCampaignPortalSyncs,
  }
}

export default useCampaignPortalSyncs
